import { Component, Inject, input, signal } from '@angular/core';
import { Grid, GridSettingsState, TableColumn } from '../grid.model';
import { GridService } from '../../services/grid.service';
import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { FilterItem } from '../filter-component/filter-component.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LocalizePipe } from "../../pipes/LocalizePipe";
import { SettingsModalResult } from './grid-settings-modal-component.model';

@Component({
  selector: 'app-grid-settings-modal',
  imports: [DragDropModule, LocalizePipe],
  templateUrl: './grid-settings-modal-component.html',
  styleUrl: './grid-settings-modal-component.css',
})
export class GridSettingsModalComponent {
  grid = signal<Grid>({
    query: '',
        authorizationRoute: '',
        extension: '',
        extensionNavigation: {
          id: '',
          columns: [],
          filters: []
        },
        columns: [],
        filters: [],
        actions: [],
        options: {
          pagesize: 0,
          ordercol: '',
          orderasc: true,
          emptymessage: '',
          export: false
        }
  });
  allColls = signal<TableColumn[]>([]);
  visibleColumns = signal<TableColumn[]>([]);
  hiddenColumns = signal<TableColumn[]>([]);
  allFilters = signal<FilterItem[]>([]);
  visibleFilters = signal<FilterItem[]>([]);
  hiddenFilters = signal<FilterItem[]>([]);
  pageSize = signal<number>(10);

  private settingsKey = '';

  constructor(private gridService: GridService,
    public dialogRef: MatDialogRef<GridSettingsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.allColls.set(data.allColls);
    this.allFilters.set(data.allFilters);
    this.grid.set(data.grid);
    this.settingsKey = data.settingsKey || data.grid.query;
    this.loadState();
  }

  //nacteni stavu
  loadState() {
    const savedSettings = this.gridService.getSettings(this.settingsKey);

    if (savedSettings) {
      this.visibleColumns.set([]);
      this.hiddenColumns.set([...this.allColls()]);

      savedSettings.visibleColumns.forEach(name => {
        const foundIndex = this.hiddenColumns().findIndex(c => c.name === name);
        if (foundIndex > -1) {
          this.visibleColumns.set([...this.visibleColumns(), this.hiddenColumns()[foundIndex]]);
          this.hiddenColumns.update(cols  => cols.filter((_, i) => i !== foundIndex));
        }
      });

      this.visibleFilters.set([]);
      this.hiddenFilters.set([...this.allFilters()]);

      savedSettings.visibleFilters.forEach(column => {
        const foundIndex = this.hiddenFilters().findIndex(f => f.column === column);
        if (foundIndex > -1) {
          this.visibleFilters.set([...this.visibleFilters(), this.hiddenFilters()[foundIndex]]);
          this.hiddenFilters.update(filters  => filters.filter((_, i) => i !== foundIndex));
        }
      });

      this.pageSize.set(this.sanitizePageSize(savedSettings.pageSize) ?? this.sanitizePageSize(this.grid().options.pagesize) ?? 10);
    }
    else {
      this.resetToDefaults();
    }
  }

  onPageSizeInput(value: string) {
    const n = this.sanitizePageSize(Number(value));
    if (n) this.pageSize.set(n);
  }

  private sanitizePageSize(value: number | undefined | null): number | undefined {
    if (value === null || value === undefined) return undefined;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
  }

  drop(event: CdkDragDrop<any[]>){
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex,event.currentIndex);
    }
    else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
  }

  storno()
  {
    this.dialogRef.close();
  }

  saveAndClose() {
    // zachováme ručně nastavené šířky sloupců uložené mimo tento modal
    const existing = this.gridService.getSettings(this.settingsKey);
    const state: GridSettingsState = {
      visibleColumns: this.visibleColumns().map(c => c.name),
      visibleFilters: this.visibleFilters().map(f => f.column),
      pageSize: this.pageSize(),
      columnWidths: existing?.columnWidths
    };

    this.gridService.saveSettings(this.settingsKey, state);
    const settingsModalResult: SettingsModalResult = {
      visibleColumns: this.visibleColumns(),
      visibleFilters: this.visibleFilters(),
      pageSize: this.pageSize()
    }
    this.dialogRef.close(settingsModalResult);
  }

  resetToDefaults() {
    this.visibleColumns.set([...this.grid().columns.filter(column => column.visible !== false)]);
    this.hiddenColumns.set([...this.grid().extensionNavigation.columns.filter(column => column.visible !== false)]);
    this.visibleFilters.set([...this.grid().filters]);
    this.hiddenFilters.set([...this.grid().extensionNavigation.filters]);
    this.pageSize.set(this.sanitizePageSize(this.grid().options.pagesize) ?? 10);
  }
}
